import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  CssBaseline,
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Paper,
  styled,
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { confirmResetPassword, validateToken } from '../services/auth.service';
import { ErrorAlert } from '../components/common/ErrorAlert';
import { SuccessAlert } from '../components/common/SuccessAlert';

export interface ResetPasswordProps {
  headerText: string;
  buttonText: string;
  successMessage: string;
}

const StyledPageContainer = styled(Box)({
  minHeight: '100vh',
  width: '100vw',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
});

const StyledResetCard = styled(Paper)({
  background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
  borderRadius: '24px',
  boxShadow: '0 20px 40px rgba(102, 126, 234, 0.15), 0 10px 20px rgba(102, 126, 234, 0.1)',
  padding: '48px 40px',
  width: '100%',
  maxWidth: '440px',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
});

const StyledHeaderContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: '32px',
  gap: '12px',
});

const StyledTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: '28px',
  background: 'linear-gradient(145deg, #667eea, #764ba2)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  textAlign: 'center',
  marginBottom: '8px',
});

const StyledSubtitle = styled(Typography)({
  fontWeight: 400,
  color: 'rgba(0, 0, 0, 0.7)',
  textAlign: 'center',
  lineHeight: '1.5',
  fontSize: '14px',
});

const StyledTextField = styled(TextField)({
  marginBottom: '24px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    background: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(10px)',
    '&:hover fieldset': {
      borderColor: '#667eea',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#667eea',
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#667eea',
  },
});

const StyledButton = styled(Button)({
  borderRadius: '12px',
  padding: '14px 28px',
  fontSize: '16px',
  fontWeight: 600,
  textTransform: 'none',
  marginBottom: '16px',
  transition: 'all 0.3s ease',
  '&:not(:disabled)': {
    background: 'linear-gradient(145deg, #667eea, #764ba2)',
    boxShadow: '0 8px 16px rgba(102, 126, 234, 0.3)',
    '&:hover': {
      background: 'linear-gradient(145deg, #5a6fd8, #6a42a0)',
      boxShadow: '0 12px 24px rgba(102, 126, 234, 0.4)',
      transform: 'translateY(-2px)',
    },
  },
  '&:disabled': {
    background: 'rgba(0, 0, 0, 0.12)',
    color: 'rgba(0, 0, 0, 0.26)',
  },
});

const StyledTextButton = styled(Button)({
  textTransform: 'none',
  color: '#667eea',
  fontWeight: 500,
  borderRadius: '8px',
  padding: '8px 16px',
  '&:hover': {
    background: 'rgba(102, 126, 234, 0.1)',
  },
});

const StyledCenterBox = styled(Box)({
  textAlign: 'center',
});

type ResetState = 'loading' | 'form' | 'success' | 'token-expired';

export default function ResetPassword(props: ResetPasswordProps): JSX.Element {
  const { token } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [resetState, setResetState] = useState<ResetState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const validateResetToken = async (): Promise<void> => {
      if (!token) {
        setError('Invalid reset link. Missing token.');
        setResetState('token-expired');
        return;
      }

      try {
        const response = await validateToken(token);
        if (response instanceof Error) {
          setError(response.message);
          setResetState('token-expired');
        } else {
          setResetState('form');
        }
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Token validation failed';
        setError(errorMessage);
        setResetState('token-expired');
      }
    };

    validateResetToken();
  }, [token]);

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setPassword(event.target.value);
    if (error) setError(null);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    if (!token) {
      setError('Invalid reset link. Missing token.');
      return;
    }

    const passwordValidation = validatePassword(password);
    if (passwordValidation) {
      setError(passwordValidation);
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const response = await confirmResetPassword(token, password);
      if (response instanceof Error) {
        setError(response.message);
      } else {
        setPassword('');
        setResetState('success');
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = (): void => {
    navigate('/login');
  };

  const handleRequestNewLink = (): void => {
    navigate('/forgot-password');
  };

  const togglePasswordVisibility = (): void => {
    setShowPassword(!showPassword);
  };

  const validatePassword = (password: string): string | null => {
    if (!password.trim()) {
      return 'Please enter a new password.';
    }
    if (password.length < 8) {
      return 'Password must be at least 8 characters long.';
    }
    if (!/(?=.*[a-z])/.test(password)) {
      return 'Password must contain at least one lowercase letter.';
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      return 'Password must contain at least one uppercase letter.';
    }
    if (!/(?=.*\d)/.test(password)) {
      return 'Password must contain at least one number.';
    }
    return null;
  };

  const isPasswordValid = validatePassword(password) === null;

  if (resetState === 'loading') {
    return (
      <>
        <CssBaseline />
        <StyledPageContainer>
          <StyledResetCard elevation={0}>
            <StyledCenterBox>
              <Typography variant="h6" color="text.secondary">
                Validating reset link...
              </Typography>
            </StyledCenterBox>
          </StyledResetCard>
        </StyledPageContainer>
      </>
    );
  }

  return (
    <>
      <CssBaseline />
      <StyledPageContainer>
        <StyledResetCard elevation={0}>
          <StyledHeaderContainer>
            <StyledTitle>
              {props.headerText}
            </StyledTitle>
            <StyledSubtitle>
              Password must be at least 8 characters long and contain a mix of letters, numbers, and special characters.
            </StyledSubtitle>
          </StyledHeaderContainer>

          {resetState === 'token-expired' && (
            <>
              <ErrorAlert
                severity="error"
                title="Invalid Reset Link"
                message={error || 'This password reset link has expired or is invalid.'}
              />
              <StyledCenterBox>
                <StyledTextButton onClick={handleRequestNewLink}>
                  Request New Password Reset Link
                </StyledTextButton>
                <Box sx={{ marginTop: 1 }}>
                  <StyledTextButton onClick={handleBackToLogin}>
                    Back to Login
                  </StyledTextButton>
                </Box>
              </StyledCenterBox>
            </>
          )}

          {resetState === 'form' && (
            <Box component="form" onSubmit={handleSubmit} noValidate>
              {error && (
                <ErrorAlert
                  severity="error"
                  title="Password Reset Failed"
                  message={error}
                />
              )}
              <StyledTextField
                type={showPassword ? 'text' : 'password'}
                required
                fullWidth
                id="password"
                label="New Password"
                name="password"
                value={password}
                onChange={handlePasswordChange}
                autoComplete="new-password"
                autoFocus
                variant="outlined"
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={togglePasswordVisibility}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                        disabled={loading}
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <StyledButton
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || !isPasswordValid}
              >
                {loading ? 'Resetting Password...' : props.buttonText}
              </StyledButton>
              <StyledCenterBox>
                <StyledTextButton onClick={handleBackToLogin} disabled={loading}>
                  Back to Login
                </StyledTextButton>
              </StyledCenterBox>
            </Box>
          )}

          {resetState === 'success' && (
            <>
              <SuccessAlert
                title="Password Reset Successfully!"
                message="Your password has been updated. You can now sign in with your new password."
              />
              <StyledCenterBox>
                <StyledTextButton onClick={handleBackToLogin}>
                  Back to Login
                </StyledTextButton>
              </StyledCenterBox>
            </>
          )}
        </StyledResetCard>
      </StyledPageContainer>
    </>
  );
}
